import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import * as businessServices from "../../services/business.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import { useTranslation } from "react-i18next";
import { useRef } from "react";

const BusinessEdit = () => {
  const formikRef = useRef();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);

  const [initialValues, setInitialValues] = useState({
    description: "",
    meta_tags: "",
    type: "",
    address: "",
    profile_image: "",
    business_name: "",
    vat_number: "",
    brochure: "",
    category: "",
    sub_category: "",
    country: "",
    city: "",
    state: "",
    zip_code: "",
    offer_percentage: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [brochurePreview, setBrochurePreview] = useState("");

  const breadcrumbs = [
    { title: t("admin.faq.link_dashboard"), url: "/admin/dashboard" },
    { title: "Business", url: "/admin/business/list/1" },
    { title: t("admin.faq.link_edit"), url: "" },
  ];

  useEffect(() => {
    // Load initial data
    Promise.all([
      businessServices.Details(id),
      businessServices.getCategories(),
      // Add your API calls for countries etc.
    ])
      .then((responses) => {
        console.log(responses);
        const detailsRes = responses[0];
        const categoriesRes = responses[1] || [{ id: "1", name: "Technology" }];

        const data = detailsRes?.data || {};

        console.log("Business data loaded:", data); // Debug log

        // Extract category ID properly
        const categoryId = data.category_id?.id || data.category_id?._id || data.category_id || "";
        const subCategoryId = data.subcategory_id?.id || data.subcategory_id?._id || data.subcategory_id || "";

        setInitialValues({
          description: data.description || "",
          meta_tags: data.meta_tags || "",
          address: data.address || "",
          profile_image: "", // Don't set file object as initial value
          business_name: data.business_name || "",
          vat_number: data.vat_number || "",
          brochure: "", // Don't set file object as initial value
          category: categoryId, // Use the extracted categoryId
          sub_category: subCategoryId, // Use the extracted subCategoryId
          country: data.country || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          offer_percentage: data.offer_percentage || "",
          newPassword: "",
          confirmNewPassword: "",
        });

        if (data.vat_number && data.vat_number.length !== 15) {
          formikRef.current?.setFieldTouched("vat_number", true, true);
        }

        // Set image previews from existing data
        setProfileImagePreview(data.profile_image || "");
        setBrochurePreview(data.brochure || "");

        // Set categories if response contains them
        if (categoriesRes?.data?.list) {
          setCategories(categoriesRes.data.list);
        }

        // If category is selected, load subcategories
        if (categoryId) {
          console.log("Loading subcategories for category:", categoryId);
          loadSubCategories(categoryId);
        }

        setDataLoaded(true);
      })
      .catch((error) => {
        console.log("Fetch error: ", error);
        setDataLoaded(true); // Set to true even on error to show the form
      });
  }, [id]);

  // Also update your loadSubCategories function to handle errors:
  const loadSubCategories = (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      return;
    }

    console.log("Loading subcategories for categoryId:", categoryId);

    businessServices
      .getSubCategories(categoryId)
      .then((response) => {
        if (response.success && response.data?.list) {
          console.log("Subcategories loaded:", response.data.list);
          setSubCategories(response.data.list);
        } else {
          console.log("No subcategories found or API error");
          setSubCategories([]);
        }
      })
      .catch((error) => {
        console.log("Error loading subcategories:", error);
        setSubCategories([]);
      });
  };

  const handleCategoryChange = (categoryId, setFieldValue) => {
    console.log(categoryId);
    setFieldValue("category", categoryId);
    setFieldValue("sub_category", ""); // Reset subcategory
    loadSubCategories(categoryId);
  };

  // Updated file validation function
  const validateFile = (file, fieldName) => {
    if (!file) return null; // File is optional for edit form

    const supportedFormats = ["image/jpeg", "image/png", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!supportedFormats.includes(file.type)) {
      return `${fieldName} must be a valid image format (JPEG, PNG, JPG)`;
    }

    if (file.size > maxSize) {
      return `${fieldName} size must be less than 5MB`;
    }

    return null;
  };

  // Updated handleFileChange function
  const handleFileChange = (event, fieldName, setFieldValue, setPreview, setFieldTouched) => {
    const file = event.target.files[0];

    if (file) {
      // Set the field value
      setFieldValue(fieldName, file);

      // Mark field as touched to show validation errors
      setFieldTouched(fieldName, true);

      // Only show preview for valid file types
      const supportedFormats = ["image/jpeg", "image/png", "image/jpg"];
      if (supportedFormats.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        // Clear preview for invalid files
        setPreview("");
      }
    } else {
      // Clear field and preview if no file selected
      setFieldValue(fieldName, "");
      setFieldTouched(fieldName, true);
      setPreview("");
    }
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded && (
        <Formik
          innerRef={formikRef}
          enableReinitialize={true}
          initialValues={initialValues}
          validateOnBlur={false}
          validate={(values) => {
            const error = {};

            if (!values.description || !values.description.trim()) error.description = "admin.faq.label_description_error";

            if (!values.business_name || !values.business_name.trim()) error.business_name = "Business name is required";

            if (!values.address || !values.address.trim()) error.address = "Address is required";

            if (values.vat_number && !/^[A-Z0-9]{8,15}$/.test(values.vat_number)) error.vat_number = "Invalid VAT number format";
            if (values.vat_number && values.vat_number.length !== 15) {
              error.vat_number = "Invalid VAT number must be 15 digits";
            }

            if (!values.category) error.category = "Category is required";
            if (!values.sub_category) error.sub_category = "Sub category is required";
            if (!values.country) error.country = "Country is required";
            if (!values.city || !values.city.trim()) error.city = "City is required";
            if (!values.state || !values.state.trim()) error.state = "State is required";

            if (values.zip_code && !/^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/.test(values.zip_code)) error.zip_code = "Invalid post code format";

            // Password validation when password change is enabled
            if (isPasswordChanged) {
              if (!values.newPassword || !values.newPassword.trim()) {
                error.newPassword = "New password is required";
              } else if (values.newPassword.length < 6) {
                error.newPassword = "Password must be at least 6 characters long";
              }

              if (!values.confirmNewPassword || !values.confirmNewPassword.trim()) {
                error.confirmNewPassword = "Confirm password is required";
              } else if (values.newPassword !== values.confirmNewPassword) {
                error.confirmNewPassword = "Passwords do not match";
              }
            }

            // File validation
            if (values.profile_image && values.profile_image instanceof File) {
              const profileImageError = validateFile(values.profile_image, "Profile image");
              if (profileImageError) error.profile_image = profileImageError;
            }

            if (values.brochure && values.brochure instanceof File) {
              const brochureError = validateFile(values.brochure, "Brochure");
              if (brochureError) error.brochure = brochureError;
            }

            return error;
          }}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitted(true);
            globalLoader(true);
            let formData = new FormData();
            console.log(values);
            formData.append("id", id);
            formData.append("businessName", values.business_name?.trim());
            formData.append("description", values.description.trim());
            formData.append("address", values.address.trim());
            formData.append("business_name", values.business_name.trim());
            formData.append("vatNumber", values.vat_number);
            formData.append("categoryId", values.category);
            formData.append("subCategoryId", values.sub_category);
            formData.append("country", values.country);
            formData.append("city", values.city.trim());
            formData.append("state", values.state.trim());
            formData.append("zipCode", values.zip_code);
            formData.append("offerPercentage", values.offer_percentage);

            if (isPasswordChanged) {
              formData.append("newPassword", values.newPassword);
              formData.append("confirmNewPassword", values.confirmNewPassword);
            }

            // Handle file uploads
            if (values.profile_image instanceof File) {
              formData.append("profile_image", values.profile_image);
            }
            if (values.brochure instanceof File) {
              formData.append("brochure", values.brochure);
            }

            console.log(formData, "handle submit run");

            businessServices
              .Edit(formData)
              .then((response) => {
                setSubmitting(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message,
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => {
                    globalLoader(false);
                    navigate(`/admin/business/list/1`); // Fixed navigation URL
                  }, 1000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                  globalLoader(false);
                }
              })
              .catch((error) => {
                console.log("Submit error: ", error);
                setSubmitted(false);
                globalLoader(false);
              });
          }}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched }) => (
            <form onSubmit={handleSubmit}>
              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div>
                        <h6 className="main-content-label mb-3">Edit Business</h6>
                      </div>
                      <div className="row row-sm">
                        {/* Business Name */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="business_name" className="text-left d-flex">
                            Business Name:<span className="requirestar">*</span>
                          </label>
                          <input name="business_name" id="business_name" type="text" onChange={handleChange} onBlur={handleBlur} value={values.business_name} className="form-control" placeholder="Enter business name" />
                          <span className="text-danger d-flex text-left">{errors.business_name && touched.business_name && t(errors.business_name)}</span>
                        </div>

                        {/* VAT Number */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="vat_number" className="text-left d-flex">
                            VAT Number:
                          </label>
                          <input name="vat_number" id="vat_number" type="text" onChange={handleChange} onBlur={handleBlur} value={values.vat_number} className="form-control" placeholder="Enter VAT number" />
                          <span className="text-danger d-flex text-left">{errors.vat_number && touched.vat_number && t(errors.vat_number)}</span>
                        </div>

                        {/* Offer Percentage */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="offer_percentage" className="text-left d-flex">
                            Offer Percentage:
                          </label>
                          <input name="offer_percentage" id="offer_percentage" type="number" onChange={handleChange} onBlur={handleBlur} value={values.offer_percentage} className="form-control" placeholder="Enter offer percentage" min="0" max="100" />
                          <span className="text-danger d-flex text-left">{errors.offer_percentage && touched.offer_percentage && t(errors.offer_percentage)}</span>
                        </div>

                        {/* Category */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="category" className="text-left d-flex">
                            Category:<span className="requirestar">*</span>
                          </label>
                          <div className="select-down-arrow">
                            <select
                              className="form-control"
                              name="category"
                              id="category"
                              onChange={(e) => {
                                handleCategoryChange(e.target.value, setFieldValue);
                                setFieldTouched("category", true);
                              }}
                              onBlur={handleBlur}
                              value={values.category}>
                              <option value="" disabled>
                                Select Category
                              </option>
                              {categories.map((cat) => (
                                <option key={cat._id} value={cat._id}>
                                  {cat.name || cat.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="text-danger d-flex text-left">{errors.category && touched.category && t(errors.category)}</span>
                        </div>

                        {/* Sub Category */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="sub_category" className="text-left d-flex">
                            Sub Category:<span className="requirestar">*</span>
                          </label>
                          <div className="select-down-arrow">
                            <select
                              className="form-control"
                              name="sub_category"
                              id="sub_category"
                              onChange={(e) => {
                                setFieldValue("sub_category", e.target.value);
                                setFieldTouched("sub_category", true);
                              }}
                              onBlur={handleBlur}
                              value={values.sub_category}
                              disabled={!values.category}>
                              <option value="" disabled>
                                Select Sub Category
                              </option>
                              {subCategories.map((subCat) => (
                                <option key={subCat._id} value={subCat._id}>
                                  {subCat.name || subCat.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="text-danger d-flex text-left">{errors.sub_category && touched.sub_category && t(errors.sub_category)}</span>
                        </div>

                        {/* Country */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="country" className="text-left d-flex">
                            Country:<span className="requirestar">*</span>
                          </label>
                          <div>
                            <input name="country" id="country" type="text" onChange={handleChange} onBlur={handleBlur} value={values.country} className="form-control" placeholder="Enter country" />
                          </div>
                          <span className="text-danger d-flex text-left">{errors.country && touched.country && t(errors.country)}</span>
                        </div>

                        {/* City */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="city" className="text-left d-flex">
                            City:<span className="requirestar">*</span>
                          </label>
                          <input name="city" id="city" type="text" onChange={handleChange} onBlur={handleBlur} value={values.city} className="form-control" placeholder="Enter city" />
                          <span className="text-danger d-flex text-left">{errors.city && touched.city && t(errors.city)}</span>
                        </div>

                        {/* State */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="state" className="text-left d-flex">
                            State:<span className="requirestar">*</span>
                          </label>
                          <input name="state" id="state" type="text" onChange={handleChange} onBlur={handleBlur} value={values.state} className="form-control" placeholder="Enter state" />
                          <span className="text-danger d-flex text-left">{errors.state && touched.state && t(errors.state)}</span>
                        </div>

                        {/* Post Code */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="zip_code" className="text-left d-flex">
                            Post Code:
                          </label>
                          <input name="zip_code" id="zip_code" type="text" onChange={handleChange} onBlur={handleBlur} value={values.zip_code} className="form-control" placeholder="Enter post code" />
                          <span className="text-danger d-flex text-left">{errors.zip_code && touched.zip_code && t(errors.zip_code)}</span>
                        </div>

                        {/* Address */}
                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="address" className="text-left d-flex">
                            Address:<span className="requirestar">*</span>
                          </label>
                          <textarea id="address" name="address" className="form-control" rows="3" value={values.address} onChange={handleChange} onBlur={handleBlur} placeholder="Enter complete address" />
                          <span className="text-danger d-flex text-left">{errors.address && touched.address && t(errors.address)}</span>
                        </div>

                        {/* Description */}
                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="description" className="text-left d-flex">
                            {t("admin.faq.label_description")}:<span className="requirestar">*</span>
                          </label>
                          <textarea id="description" name="description" className="form-control" rows="5" value={values.description} onChange={handleChange} onBlur={handleBlur} />
                          <span className="text-danger d-flex text-left">{errors.description && touched.description && t(errors.description)}</span>
                        </div>

                        {/* Password Change Section */}
                        <div className="col-lg-12 text-center form-group" style={{ marginTop: "0.5rem" }}>
                          <div className="">
                            <label htmlFor="isPasswordChanged" className="ckbox cp">
                              <p className="text-start" style={{ paddingLeft: "1.5rem", paddingTop: "0.1rem" }}>
                                Check, if you want to reset password
                              </p>
                              <input id="isPasswordChanged" value={isPasswordChanged} type="checkbox" className="form-check select-check cp" onChange={() => setIsPasswordChanged(!isPasswordChanged)} />
                              <span></span>
                            </label>
                          </div>

                          {isPasswordChanged && (
                            <div style={{ marginTop: "-1.5rem" }}>
                              <div>
                                <label className="text-left d-flex" htmlFor="newPassword">
                                  New Password:<span className="requirestar">*</span>
                                </label>
                                <input id="newPassword" name="newPassword" type="password" onChange={handleChange} onBlur={handleBlur} value={values.newPassword} className="form-control" placeholder="Enter new password" />
                                <span className="text-danger d-flex text-left">{errors.newPassword && touched.newPassword && t(errors.newPassword)}</span>
                              </div>

                              <div style={{ marginTop: "1rem" }}>
                                <label className="text-left d-flex" htmlFor="confirmNewPassword">
                                  Confirm New Password:<span className="requirestar">*</span>
                                </label>
                                <input id="confirmNewPassword" name="confirmNewPassword" type="password" onChange={handleChange} onBlur={handleBlur} value={values.confirmNewPassword} className="form-control" placeholder="Enter confirm new password" />
                                <span className="text-danger d-flex text-left">{errors.confirmNewPassword && touched.confirmNewPassword && t(errors.confirmNewPassword)}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Profile Image */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="profile_image" className="text-left d-flex">
                            Profile Image:
                          </label>
                          <input name="profile_image" id="profile_image" type="file" accept="image/*" onChange={(e) => handleFileChange(e, "profile_image", setFieldValue, setProfileImagePreview, setFieldTouched)} className="form-control" />
                          <span className="text-danger d-flex text-left">{errors.profile_image && touched.profile_image && errors.profile_image}</span>
                          {profileImagePreview && (
                            <div className="mt-2 text-left">
                              <img src={profileImagePreview} alt="Profile Preview" style={{ maxWidth: "150px", maxHeight: "150px", objectFit: "cover" }} className="img-thumbnail" />
                            </div>
                          )}
                        </div>

                        {/* Brochure */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="brochure" className="text-left d-flex">
                            Brochure:
                          </label>
                          <input name="brochure" id="brochure" type="file" accept="image/*" onChange={(e) => handleFileChange(e, "brochure", setFieldValue, setBrochurePreview, setFieldTouched)} className="form-control" />
                          <span className="text-danger d-flex text-left">{errors.brochure && touched.brochure && errors.brochure}</span>
                          {brochurePreview && (
                            <div className="mt-2 text-left">
                              <img src={brochurePreview} alt="Brochure Preview" style={{ maxWidth: "150px", maxHeight: "150px", objectFit: "cover" }} className="img-thumbnail" />
                            </div>
                          )}
                        </div>

                        <div className="">
                          <button className="btn btn-main-primary signbtn mr-2" type="submit" disabled={submitted}>
                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                            {t("Update")}
                          </button>
                          <button className="btn ripple btn-secondary" type="button" disabled={submitted} onClick={() => navigate(-1)}>
                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                            {t("Cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
    </>
  );
};

export default BusinessEdit;
